import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as SplitScreenIcon } from 'assets/icons/Titasz/Split-screen.svg';

import { ColumnProps, useDialogState } from 'components';
import { PageHeaderProps } from 'components/page/PageHeader';
import { TableViewCreationDialog } from 'components/table/components/TableViewCreationDialog';
import { Env } from 'config/env';
import { useTableViewsSetting } from 'features/tableViews';
import { primary } from 'theme/colors';
import * as qs from 'utils/query';
import {
  applyColumnConfig,
  areColumnConfigsEqual,
  ColumnDropPosition,
  reorderColumnsByKey,
  resetColumnsToDefault,
  serializeColumnsForUrl,
  snapshotColumns,
} from 'utils/tableViews';
import { mergeSx } from 'utils/styles';

const testIdAttr = (id: string) => ({ [Env.TEST_ID_ATTRIBUTE_KEY]: id });

const styles = {
  settingsHeader: {
    paddingBottom: 3,
  },
  settingsAction: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 2,
  },
  menuWrapper: {
    width: '340px',
    padding: 2,
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1,
    position: 'relative',
  },
  menuItemDragging: {
    opacity: 0.45,
  },
  dropIndicatorBefore: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 8,
      right: 8,
      top: 0,
      height: 2,
      backgroundColor: 'primary.main',
      borderRadius: 1,
      zIndex: 1,
    },
  },
  dropIndicatorAfter: {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 8,
      right: 8,
      bottom: 0,
      height: 2,
      backgroundColor: 'primary.main',
      borderRadius: 1,
      zIndex: 1,
    },
  },
  menuItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
    flex: 1,
  },
  dragHandle: {
    cursor: 'grab',
    color: 'action.active',
    display: 'flex',
    alignItems: 'center',
  },
  tableCount: {
    backgroundColor: primary[400],
    padding: '2px 6px',
    borderRadius: '100px',
  },
  endIcon: {
    '& .MuiButton-endIcon >*:nth-of-type(1)': { fontSize: '10px' },
    whiteSpace: 'noWrap',
  },
  viewsSection: {
    pt: 2,
  },
  viewsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    pb: 1.5,
  },
  chip: {
    '.MuiChip-label': {
      p: 0,
      mx: 1.5,
    },
  },
  chipTypography: {
    maxWidth: 180,
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  viewsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    pb: 1,
  },
};

const TableView = ({
  setColumnsState,
  validColumns,
  onTableColumnVisibilityChange,
  defaultColumns,
}: PageHeaderProps<unknown>) => {
  const location = useLocation();
  const { views, activeViewId, isLoading, isSaving, saveView, selectView, deleteView } =
    useTableViewsSetting(location.pathname);

  const isAnyHidden = useCallback(
    () => validColumns?.some((column) => column.hidden),
    [validColumns]
  );
  const visibleColumns = validColumns?.reduce(
    (acc, column) => (column.hidden || column.customCheckboxCell ? acc : acc + 1),
    0
  );

  const [isAnyColumnHidden, setIsAnyColumnHidden] = useState(isAnyHidden);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    key: string;
    position: ColumnDropPosition;
  } | null>(null);
  const appliedActiveViewRef = useRef<string | null>(null);
  const prevHasUrlColumnsRef = useRef(Boolean(qs.parse(location.search).columns));
  // the user edited the columns by hand, so the active view must not be re-applied
  const hasManualColumnChangeRef = useRef(false);
  const open = Boolean(anchorEl);
  const {
    isOpen: isSaveDialogOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialogState();

  useEffect(() => {
    setIsAnyColumnHidden(isAnyHidden);
  }, [validColumns, isAnyHidden]);

  const syncColumnsToUrl = useCallback(
    (colArray: (ColumnProps<unknown> | null)[]) => {
      if (!onTableColumnVisibilityChange) {
        return;
      }

      const currentSnapshot = snapshotColumns(colArray);
      const defaultSnapshot = snapshotColumns(defaultColumns);
      const isDefault = defaultColumns && areColumnConfigsEqual(currentSnapshot, defaultSnapshot);

      onTableColumnVisibilityChange(isDefault ? undefined : serializeColumnsForUrl(colArray));
    },
    [onTableColumnVisibilityChange, defaultColumns]
  );

  const applyColumns = useCallback(
    (colArray: (ColumnProps<unknown> | null)[]) => {
      if (!setColumnsState) {
        return;
      }
      setColumnsState(colArray);
      syncColumnsToUrl(colArray);
    },
    [setColumnsState, syncColumnsToUrl]
  );

  useEffect(() => {
    if (!defaultColumns || !setColumnsState || isLoading || !activeViewId) {
      return;
    }

    const hasUrlColumns = Boolean(qs.parse(location.search).columns);

    if (prevHasUrlColumnsRef.current && !hasUrlColumns && !hasManualColumnChangeRef.current) {
      appliedActiveViewRef.current = null;
    }
    prevHasUrlColumnsRef.current = hasUrlColumns;

    if (hasUrlColumns) {
      appliedActiveViewRef.current = activeViewId;
      return;
    }

    if (appliedActiveViewRef.current === activeViewId) {
      return;
    }

    const activeView = views.find((view) => view.id === activeViewId);
    if (!activeView) {
      appliedActiveViewRef.current = null;
      return;
    }

    appliedActiveViewRef.current = activeViewId;
    const nextColumns = applyColumnConfig(defaultColumns, activeView.columns);
    setColumnsState(nextColumns);
    syncColumnsToUrl(nextColumns);
  }, [
    activeViewId,
    views,
    defaultColumns,
    setColumnsState,
    isLoading,
    syncColumnsToUrl,
    location.search,
  ]);

  const onTableViewHandler = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onColumnHandler = (key: string) => {
    if (!setColumnsState) {
      return;
    }

    hasManualColumnChangeRef.current = true;

    setColumnsState((prevState) => {
      const newColState = prevState.map((column) => {
        if (column?.key === key && !column?.cannotBeHidden) {
          return { ...column, hidden: !column.hidden };
        }
        return column;
      });

      syncColumnsToUrl(newColState);
      return newColState;
    });
  };

  const onShowHandler = () => {
    if (!setColumnsState) {
      return;
    }

    const show = !!isAnyColumnHidden;

    hasManualColumnChangeRef.current = true;

    setColumnsState((prev) => {
      const newState = prev.map((column) => {
        if (!column?.cannotBeHidden && !column?.customCheckboxCell && column) {
          return { ...column, hidden: !show };
        }
        return column;
      });

      syncColumnsToUrl(newState);
      return newState;
    });
  };

  const clearDragState = () => {
    setDragKey(null);
    setDropTarget(null);
  };

  const handleReorder = (fromKey: string, toKey: string, position: ColumnDropPosition) => {
    if (!setColumnsState || fromKey === toKey) {
      return;
    }

    hasManualColumnChangeRef.current = true;

    setColumnsState((prev) => {
      const next = reorderColumnsByKey(prev, fromKey, toKey, position);
      syncColumnsToUrl(next);
      return next;
    });
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>, columnKey: string) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (!dragKey || dragKey === columnKey) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const position: ColumnDropPosition =
      event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';

    setDropTarget((prev) =>
      prev?.key === columnKey && prev.position === position ? prev : { key: columnKey, position }
    );
  };

  const handleSaveView = async (name: string) => {
    const columns = snapshotColumns(validColumns);
    const view = await saveView(name, columns);
    appliedActiveViewRef.current = view.id;
    hasManualColumnChangeRef.current = false;
  };

  const handleSelectView = async (viewId: string) => {
    if (!defaultColumns) {
      return;
    }

    const view = await selectView(viewId);
    if (!view) {
      return;
    }

    appliedActiveViewRef.current = view.id;
    hasManualColumnChangeRef.current = false;
    applyColumns(applyColumnConfig(defaultColumns, view.columns));
  };

  const handleDeleteView = async (viewId: string) => {
    const result = await deleteView(viewId);
    hasManualColumnChangeRef.current = false;

    if (result.deletedLast && defaultColumns) {
      appliedActiveViewRef.current = null;
      applyColumns(resetColumnsToDefault(defaultColumns));
      return;
    }

    if (result.activeViewId && result.activeViewId !== activeViewId && defaultColumns) {
      const nextView = result.views.find((view) => view.id === result.activeViewId);
      if (nextView) {
        appliedActiveViewRef.current = nextView.id;
        applyColumns(applyColumnConfig(defaultColumns, nextView.columns));
      }
    }
  };

  const handleClickAway = () => {
    clearDragState();
    setAnchorEl(null);
  };

  let savedViewsContent: React.ReactNode;
  if (isLoading || isSaving) {
    savedViewsContent = (
      <Box sx={styles.viewsContainer} {...testIdAttr('table-view-loading')}>
        <CircularProgress size={20} />
      </Box>
    );
  } else if (views.length) {
    savedViewsContent = (
      <Grid container spacing={1}>
        {views.map(({ id, name }) => (
          <Grid item key={id} xs="auto">
            <Chip
              sx={styles.chip}
              color={activeViewId === id ? 'primary' : 'default'}
              variant={activeViewId === id ? 'filled' : 'outlined'}
              onClick={() => handleSelectView(id)}
              onDelete={() => handleDeleteView(id)}
              deleteIcon={<CancelIcon {...testIdAttr(`table-view-chip-delete-${id}`)} />}
              {...testIdAttr(`table-view-chip-${id}`)}
              label={
                <Tooltip
                  title={name}
                  enterDelay={Env.TOOLTIP_DELAY_SHORT}
                  enterNextDelay={Env.TOOLTIP_DELAY_SHORT}
                >
                  <Typography variant="subtitle2" sx={styles.chipTypography}>
                    {name}
                  </Typography>
                </Tooltip>
              }
            />
          </Grid>
        ))}
      </Grid>
    );
  } else {
    savedViewsContent = (
      <Typography variant="caption" color="text.secondary">
        <Trans i18nKey="TABLE.NO_SAVED_VIEWS" />
      </Typography>
    );
  }

  return (
    <>
      <Button
        sx={styles.endIcon}
        size="small"
        onClick={onTableViewHandler}
        id="basic-button"
        startIcon={<SplitScreenIcon />}
        endIcon={
          <Typography sx={styles.tableCount} variant="cellHeaderSmall">
            {`${visibleColumns}/${
              validColumns?.filter((item) => !item.customCheckboxCell)?.length
            }`}
          </Typography>
        }
        variant="contained"
        {...testIdAttr('table-view-open-button')}
      >
        <Trans i18nKey="TABLE.TABLE_VIEW" />
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClickAway}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        {...testIdAttr('table-view-menu')}
      >
        <Box sx={styles.menuWrapper}>
          <Box sx={styles.settingsHeader}>
            <Typography variant="subtitle2">
              <Trans i18nKey="TABLE.TABLE_SETTINGS" />
            </Typography>
          </Box>
          <Box sx={styles.settingsAction}>
            <Typography variant="caption">
              <Trans i18nKey="TABLE.SHOWN_IN_THE_TABLE" />
            </Typography>
            <Button
              variant="text"
              onClick={onShowHandler}
              {...testIdAttr('table-view-toggle-all-columns')}
            >
              <Typography sx={{ textTransform: 'none' }} variant="caption">
                {isAnyColumnHidden ? (
                  <Trans i18nKey="TABLE.SHOW_ALL" />
                ) : (
                  <Trans i18nKey="TABLE.HIDE_ALL" />
                )}
              </Typography>
            </Button>
          </Box>
          <Box>
            {validColumns?.map((column) => {
              if (column.customCheckboxCell) {
                return null;
              }

              const visibilityIcon = column.hidden ? (
                <VisibilityOffOutlinedIcon fontSize="small" color="disabled" />
              ) : (
                <VisibilityIcon fontSize="small" color="primary" />
              );

              const isDragging = dragKey === column.key;
              const showDropBefore =
                dropTarget?.key === column.key && dropTarget.position === 'before';
              const showDropAfter =
                dropTarget?.key === column.key && dropTarget.position === 'after';

              return (
                <MenuItem
                  key={column.key}
                  sx={mergeSx(
                    styles.menuItem,
                    isDragging ? styles.menuItemDragging : null,
                    showDropBefore ? styles.dropIndicatorBefore : null,
                    showDropAfter ? styles.dropIndicatorAfter : null
                  )}
                  onClick={() => onColumnHandler(column.key)}
                  onDragOver={(event) => handleDragOver(event, column.key)}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (dragKey && dropTarget) {
                      handleReorder(dragKey, dropTarget.key, dropTarget.position);
                    }
                    clearDragState();
                  }}
                  {...testIdAttr(`table-view-column-item-${column.key}`)}
                >
                  <Box sx={styles.menuItemContent}>
                    <Box
                      sx={styles.dragHandle}
                      draggable
                      onDragStart={(event) => {
                        event.stopPropagation();
                        setDragKey(column.key);
                        setDropTarget(null);
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={clearDragState}
                      onClick={(event) => event.stopPropagation()}
                      {...testIdAttr(`table-view-column-drag-${column.key}`)}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <Typography variant="cellLabel" noWrap>
                      {column.header || null}
                    </Typography>
                  </Box>
                  {visibilityIcon}
                </MenuItem>
              );
            })}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={styles.viewsSection}>
            <Box sx={styles.viewsHeader}>
              <Typography variant="caption">
                <Trans i18nKey="TABLE.SAVED_VIEWS" />
              </Typography>
              <Button
                variant="text"
                onClick={openSaveDialog}
                disabled={isSaving}
                {...testIdAttr('table-view-save-view-button')}
              >
                <Typography sx={{ textTransform: 'none' }} variant="caption">
                  <Trans i18nKey="TABLE.SAVE_VIEW" />
                </Typography>
              </Button>
            </Box>
            {savedViewsContent}
          </Box>
        </Box>
      </Menu>

      <TableViewCreationDialog
        open={isSaveDialogOpen}
        onClose={closeSaveDialog}
        existingNames={views.map((view) => view.name)}
        onSaveView={handleSaveView}
      />
    </>
  );
};

export default TableView;
